import { useCallback, useEffect, useState } from 'react'

import { useLocation, useHistory } from 'react-router-dom'
import styled from 'styled-components'

import { deleteProjectDocument } from '../../../api/projectDocuments'
import {
  useFetchProjectDocument,
  useListProjectDocuments,
} from '../../../api/queries/project'
import { ProjectDocument } from '../../../api/resources/project'
import { useNotifications } from '../../../contexts/notificationProvider'
import { useUserContext } from '../../../contexts/userContext'
import { useGeneratedPaths } from '../../../hooks/useGeneratedPaths'
import { useViewportWidth } from '../../../hooks/useViewportWidth'
import { BREAKPOINTS, media } from '../../../styles/breakpoints'
import { StandardButton } from '../../common/StandardButton'
import { MainPanel } from '../../layout/main-panel/MainPanel'

import { ReportsActionSlot } from './ReportsActionSlot'
import { ReportsDocument } from './ReportsDocument'
import { ReportsFiltersSlot } from './ReportsFiltersSlot'
import { ReportsGrouping } from './ReportsGrouping'
import { ReportsLabelSlot } from './ReportsLabelSlot'

interface ReportsPageProps {
  projectPublicId: string
}

export const ReportsPage = ({ projectPublicId }: ReportsPageProps) => {
  const { generateProjectReportsNewPath } = useGeneratedPaths()
  const { state: userState } = useUserContext()
  const isSuperUser = userState.user_type === 'super'
  const { data: documentsList, refetch: refetchDocumentsList } =
    useListProjectDocuments(projectPublicId)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedDocument, setSelectedDocument] =
    useState<ProjectDocument | null>(null)
  const [deletingDocument, setDeletingDocument] = useState(false)
  const { data: document } = useFetchProjectDocument(
    projectPublicId,
    selectedDocument?.id,
  )
  const { addNotification } = useNotifications()

  const location = useLocation()
  const history = useHistory()
  const viewportWidth = useViewportWidth()
  const isMobileLayout = viewportWidth < BREAKPOINTS.tablet
  const [mobilePane, setMobilePane] = useState<'list' | 'document'>('list')

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const documentId = queryParams.get('documentId')

    if (documentId && documentsList) {
      const foundDocument = documentsList.find(
        doc => doc.id === Number(documentId),
      )
      if (foundDocument) {
        setSelectedDocument(foundDocument)
        if (isMobileLayout) {
          setMobilePane('document')
        }
        return
      }
    }

    if (!selectedDocument && documentsList?.length) {
      const mostRecentDocument = documentsList.reduce((latest, current) =>
        new Date(latest.date) > new Date(current.date) ? latest : current,
      )
      setSelectedDocument(mostRecentDocument)
      if (!isMobileLayout) {
        return
      }
      setMobilePane('list')
    }
  }, [selectedDocument, documentsList, location.search, isMobileLayout])

  const handleSelectCategory = useCallback(
    (category: string | null) => {
      setSelectedCategory(category)
    },
    [setSelectedCategory],
  )

  const handleSelectDocument = useCallback(
    (document: ProjectDocument) => {
      setSelectedDocument(document)
      const queryParams = new URLSearchParams(location.search)
      queryParams.set('documentId', document.id.toString())
      history.replace({ search: queryParams.toString() })
      if (isMobileLayout) {
        setMobilePane('document')
      }
    },
    [history, location.search, isMobileLayout],
  )

  const handleMobileBackToList = useCallback(() => {
    setMobilePane('list')
  }, [])

  const showListLane = !isMobileLayout || mobilePane === 'list'
  const showDocumentLane = !isMobileLayout || mobilePane === 'document'

  const displayDocument = document ?? selectedDocument ?? null
  const categoryForDelete =
    document?.category ?? selectedDocument?.category ?? ''
  const allowDeleteReport =
    isSuperUser &&
    !!categoryForDelete &&
    ['safety', 'progress'].includes(categoryForDelete.toLowerCase())

  const handleDeleteDocument = useCallback(async () => {
    const docToDelete = document ?? selectedDocument
    if (!docToDelete || deletingDocument) return
    const shouldDelete = window.confirm(
      `Delete report "${docToDelete.name}"? This cannot be undone.`,
    )
    if (!shouldDelete) return

    try {
      setDeletingDocument(true)
      await deleteProjectDocument(projectPublicId, docToDelete.id)
      const updated = await refetchDocumentsList()
      const nextDocs = updated.data ?? []
      const nextSelected = nextDocs.reduce<ProjectDocument | null>(
        (latest, current) => {
          if (!latest) return current
          return new Date(latest.date) > new Date(current.date)
            ? latest
            : current
        },
        null,
      )
      setSelectedDocument(nextSelected)
      const queryParams = new URLSearchParams(location.search)
      if (nextSelected) {
        queryParams.set('documentId', nextSelected.id.toString())
      } else {
        queryParams.delete('documentId')
      }
      history.replace({ search: queryParams.toString() })
      addNotification('Report deleted.', 'success')
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } } | undefined)
        ?.response?.status
      if (status === 403) {
        addNotification('Super users only.', 'error')
      } else {
        addNotification('Failed to delete report.', 'error')
      }
    } finally {
      setDeletingDocument(false)
    }
  }, [
    addNotification,
    deletingDocument,
    document,
    selectedDocument,
    history,
    location.search,
    projectPublicId,
    refetchDocumentsList,
  ])

  return (
    <MainPanel>
      <MainPanel.Content>
        {showListLane && (
          <MainPanel.Lane maxWidth="250px">
            <MainPanel.Bar>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  width: '100%',
                }}
              >
                {isSuperUser && (
                  <StandardButton
                    primary
                    onClick={() =>
                      history.push(
                        generateProjectReportsNewPath(projectPublicId),
                      )
                    }
                  >
                    Create new report
                  </StandardButton>
                )}
                <ReportsFiltersSlot
                  documentsList={documentsList}
                  onSelectCategory={handleSelectCategory}
                />
              </div>
            </MainPanel.Bar>
            <ReportsGrouping
              documentsList={documentsList}
              restrictCategory={selectedCategory}
              onSelectDocument={handleSelectDocument}
            />
          </MainPanel.Lane>
        )}
        {showDocumentLane && (
          <MainPanel.Lane style={{ minHeight: 0 }}>
            {isMobileLayout && (
              <MobileBackBar type="button" onClick={handleMobileBackToList}>
                ← Back to reports
              </MobileBackBar>
            )}
            <MainPanel.Bar>
              <ReportsLabelSlot document={selectedDocument} />
              <ReportsActionSlot
                document={displayDocument}
                deleting={deletingDocument}
                allowDelete={allowDeleteReport}
                onDelete={handleDeleteDocument}
              />
            </MainPanel.Bar>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                paddingBottom: 16,
              }}
            >
              <ReportsDocument file={displayDocument?.document_s3} />
            </div>
          </MainPanel.Lane>
        )}
      </MainPanel.Content>
    </MainPanel>
  )
}

const MobileBackBar = styled.button`
  display: none;

  @media ${media.belowTablet} {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 44px;
    padding: 10px 16px;
    border: none;
    border-bottom: 1px solid #e2e8f0;
    background: #fff;
    color: #073c7a;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-sizing: border-box;
  }
`
